import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import Loading from 'react-loading';
import styled, { keyframes } from 'styled-components';

// Importar componentes reutilizáveis
import KanbanColumn from '../../../components/KanbanColumn';

// Animations
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
`;

// Container principal mobile
const MobileContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    animation: ${fadeIn} 0.3s ease-out;
`;

// Tab buttons container
const TabsContainer = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

// Tab button individual
const TabButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: fit-content;
    padding: 12px 20px;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    position: relative;
    overflow: hidden;

    ${props => props.$active ? `
        background: ${props.$color};
        color: white;
        box-shadow: 0 4px 12px ${props.$glow};
        transform: scale(1.02);
    ` : `
        background: white;
        color: #64748b;
        border: 1px solid rgba(0, 0, 0, 0.08);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        &:hover {
            background: #f8fafc;
            color: ${props.$color};
            border-color: ${props.$color}40;
        }
    `}

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.5s ease;
    }

    &:hover::before {
        left: 100%;
    }
`;

const TabIcon = styled.span`
    display: flex;
    font-size: 16px;
`;

const TabCount = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;

    ${props => props.$active ? `
        background: rgba(255, 255, 255, 0.25);
        color: white;
    ` : `
        background: ${props.$color}15;
        color: ${props.$color};
    `}
`;

// Column container
const ColumnContainer = styled.div`
    animation: ${slideIn} 0.3s ease-out;
    min-height: 500px;
`;

// Loading container
const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
`;

// Config section para coluna de análise
const ConfigSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    --padding: 16px;
    --margin: 12px 16px;
    background: white;
    --border-radius: 12px;
    --border: 1px solid rgba(0, 0, 0, 0.06);
    --box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const ConfigRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const ConfigLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
`;

const ConfigValue = styled.span`
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: #318CD5;
    padding: 3px 10px;
    background: rgba(49, 140, 213, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(49, 140, 213, 0.15);
`;

const EditButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 40px;
    margin-top: 8px;
    background: #318CD5;
    border: none;
    border-radius: 10px;
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #2778b8;
        transform: translateY(-1px);
    }
`;

// Status configuration
const statusConfig = {
    analysis: {
        color: '#8b5cf6',
        glow: 'rgba(139, 92, 246, 0.3)',
        icon: '📋'
    },
    production: {
        color: '#f59e0b',
        glow: 'rgba(245, 158, 11, 0.3)',
        icon: '🍳'
    },
    ready: {
        color: '#10b981',
        glow: 'rgba(16, 185, 129, 0.3)',
        icon: '✅'
    },
    completed: {
        color: '#06b6d4',
        glow: 'rgba(6, 182, 212, 0.3)',
        icon: '🎉'
    }
};

export function MobileKanban({
    tempoBalcaoMinBD,
    tempoBalcaoMaxBD,
    tempoEntregaMinBD,
    tempoEntregaMaxBD,
    tipoImpressao,
    pedidosAnalise,
    pedidosProducao,
    pedidosEntrega,
    pedidosFinalizado,
    filterIsLoading,
    handleShowPedido,
    handleEditTempoEntrega,
    handleAddEntregador,
    handleAvancar,
    userID,
    pedidosProcessando
}) {
    const { t } = useTranslation(['orders', 'common']);
    const [currentScreen, setCurrentScreen] = useState('analysis');

    // Map screen names to pedidos arrays
    const screenPedidos = {
        analysis: pedidosAnalise || [],
        production: pedidosProducao || [],
        ready: pedidosEntrega || [],
        completed: pedidosFinalizado || []
    };

    // Map screen names to container classes
    const containerClasses = {
        analysis: 'containerAnalise',
        production: 'containerProducao',
        ready: 'containerPronto',
        completed: 'containerFinalizado'
    };

    // Config header content for analysis column
    const AnaliseHeaderContent = () => (
        <ConfigSection>
            <ConfigRow>
                <ConfigLabel>🏪 {t('orders:config.counterTime')}:</ConfigLabel>
                <ConfigValue>{tempoBalcaoMinBD} a {tempoBalcaoMaxBD} min</ConfigValue>
            </ConfigRow>
            <ConfigRow>
                <ConfigLabel>🏍️ {t('orders:config.deliveryTime')}:</ConfigLabel>
                <ConfigValue>{tempoEntregaMinBD} a {tempoEntregaMaxBD} min</ConfigValue>
            </ConfigRow>
            <ConfigRow>
                <ConfigLabel>⚡ {t('orders:config.autoAccept')}:</ConfigLabel>
                <ConfigValue>
                    {tipoImpressao === 'manual' ? t('orders:config.disabled') :
                        tipoImpressao === 'automatico' ? t('orders:config.enabled') : t('orders:config.notDefined')}
                </ConfigValue>
            </ConfigRow>
            <EditButton onClick={() => handleEditTempoEntrega(userID)}>
                {t('orders:config.edit')}
            </EditButton>
        </ConfigSection>
    );

    // Screen labels
    const screenLabels = {
        analysis: t('orders:columns.analysis'),
        production: t('orders:columns.production'),
        ready: t('orders:columns.ready'),
        completed: t('orders:columns.completed')
    };

    // Tabs order
    const tabsOrder = ['analysis', 'production', 'ready', 'completed'];

    return (
        <MobileContainer>
            {/* Tab Navigation */}
            <TabsContainer>
                {tabsOrder.map((screen) => {
                    const config = statusConfig[screen];
                    const isActive = currentScreen === screen;
                    const count = screenPedidos[screen].length;

                    return (
                        <TabButton
                            key={screen}
                            $active={isActive}
                            $color={config.color}
                            $glow={config.glow}
                            onClick={() => setCurrentScreen(screen)}
                        >
                            <TabIcon>{config.icon}</TabIcon>
                            {screenLabels[screen]}
                            <TabCount $active={isActive} $color={config.color}>
                                {count}
                            </TabCount>
                        </TabButton>
                    );
                })}
            </TabsContainer>

            {/* Column Content */}
            <ColumnContainer>
                {filterIsLoading ? (
                    <LoadingContainer>
                        <Loading
                            type='spin'
                            color='rgb(49, 140, 213)'
                            height={40}
                            width={40}
                        />
                    </LoadingContainer>
                ) : (
                    <KanbanColumn
                        title={screenLabels[currentScreen]}
                        count={screenPedidos[currentScreen].length}
                        pedidos={screenPedidos[currentScreen]}
                        emptyMessage={t(`orders:emptyStates.${currentScreen}`)}
                        headerContent={currentScreen === 'analysis' ? <AnaliseHeaderContent /> : null}
                        containerClass={containerClasses[currentScreen]}
                        headerClass={`header-column-${currentScreen}`}
                        onShowPedido={handleShowPedido}
                        onAddEntregador={handleAddEntregador}
                        onAvancar={handleAvancar}
                        showAvancarButton={currentScreen !== 'completed'}
                        pedidosProcessando={pedidosProcessando}
                        animationDelay="0ms"
                    />
                )}
            </ColumnContainer>
        </MobileContainer>
    );
}
